import React from 'react';
import { Button } from '../ui/Button';
import { Form } from '../ui/Form';

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function getPageItems(page, totalPages) {
  const lastPage = Math.max(1, Number(totalPages) || 1);
  const currentPage = Math.min(Math.max(1, Number(page) || 1), lastPage);
  const visible = new Set([1, lastPage, currentPage]);

  if (currentPage <= 3) {
    [2, 3].filter((value) => value <= lastPage).forEach((value) => visible.add(value));
  } else if (currentPage >= lastPage - 2) {
    [lastPage - 2, lastPage - 1].filter((value) => value > 0).forEach((value) => visible.add(value));
  } else {
    visible.add(currentPage - 1);
    visible.add(currentPage + 1);
  }

  const pages = [...visible].sort((a, b) => a - b);
  return pages.flatMap((value, index) => {
    if (index === 0) return [value];
    const gap = value - pages[index - 1];
    if (gap === 2) return [value - 1, value];
    if (gap > 2) return [`ellipsis-${index}`, value];
    return [value];
  });
}

export default function Pagination({
  page = 1,
  pageSize = 10,
  totalRecords,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  disabled = false,
  showPageSize = true,
  showTotal = true,
  buttonVariant = 'primary',
  buttonSize,
  className = '',
}) {
  const lastPage = Math.max(1, Number(totalPages ?? Math.ceil((totalRecords || 0) / pageSize)) || 1);
  const activePage = Math.min(Math.max(1, Number(page) || 1), lastPage);
  const pageItems = getPageItems(activePage, lastPage);

  return (
    <div className={`tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-mt-4 ${className}`}>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
        {showPageSize && (
          <Form.Select
            aria-label="Records per page"
            value={pageSize}
            onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
            disabled={disabled}
            style={{ width: 'auto' }}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>{size} records per page</option>
            ))}
          </Form.Select>
        )}
        {showTotal && totalRecords != null && (
          <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Total Records: {totalRecords}</span>
        )}
      </div>
      <div className="tw-flex tw-items-center tw-gap-2" role="navigation" aria-label="Table pagination">
        <Button variant={buttonVariant} size={buttonSize} disabled={disabled || activePage <= 1} onClick={() => onPageChange?.(activePage - 1)} aria-label="Previous page">‹ Back</Button>
        {pageItems.map((item) => typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            aria-label={`Page ${item}`}
            aria-current={item === activePage ? 'page' : undefined}
            disabled={disabled}
            onClick={() => { if (item !== activePage) onPageChange?.(item); }}
            className={`tw-min-w-[2.25rem] tw-h-9 tw-rounded-lg tw-border-0 tw-px-2 tw-font-medium ${item === activePage ? 'tw-bg-indigo-600 tw-text-white' : 'tw-bg-indigo-50 tw-text-indigo-700 hover:tw-bg-indigo-100'} disabled:tw-opacity-50`}
          >
            {item}
          </button>
        ) : (
          <span key={item} aria-hidden="true" className="tw-px-1 tw-text-slate-500">…</span>
        ))}
        <Button variant={buttonVariant} size={buttonSize} disabled={disabled || activePage >= lastPage} onClick={() => onPageChange?.(activePage + 1)} aria-label="Next page">Next ›</Button>
        <span className="tw-sr-only">Page {activePage} of {lastPage}</span>
      </div>
    </div>
  );
}
